/*************************/
/** GET OrganisationInfo **/

import { SafeUrl } from '@angular/platform-browser';
import { OrganizationList } from './organization.model';
import { AccountFeatureLimitsData, DuplicateCommRecord, MultiSchedulerData, TagData, TagSelectorData } from './shared.model';
import { SelectorUserData, SelfCareGiverData } from './user.model';

/*************************/
export interface BroadcastUserInterest {
    id: number;
    name: string;
    checked: boolean;
}

export interface Center {
    id: number;
    name: string;
    organisation: Organisation;
}

interface Organisation {
    name: string;
}

/********************/
/** POST Broadcast **/
/********************/

export interface SendBroadcast {
    message: string;
    individual: boolean;
    audience: any[];
    channels: string[];
    centers: string[];
    translated?: Translated[];
    orgId: string;
    interest_ids?: string[];
    ph_interested?: {
        value: boolean | null;
    };
    ph_consented?: {
        value: boolean | null;
    };
    ph_info_provided?: {
        value: boolean | null;
    };
    snap_eligibility?: {
        value: boolean | null;
    };
    snap_enrollment?: {
        value: boolean | null;
    };
    snap_interest?: {
        value: boolean | null;
    };
    preview_start?: {
        date: { rowNumber: string; commDate: string }[];
        offset: string;
        timezone_identifier?: string;
        timezone_name: string;
        is_parent?: boolean;
    };
}

export interface SendWelcomeMessage {
    message: string;
    audience: number[];
    channels: string[];
    centers: string[];
    orgId: number;
    translated?: Translated[];
}

export interface Translated {
    language: string;
    message: string;
}

export class PreviewLanguageTranslationData {
    previewLanguage: string | null;
    translatedMessage: string | null;
    errorData: { type: 'error' | 'warn'; message: string } | null;

    constructor(
        previewLanguage: string | null,
        translatedMessage: string | null,
        errorData: { type: 'error' | 'warn'; message: string } | null
    ) {
        this.previewLanguage = previewLanguage || null;
        this.translatedMessage = translatedMessage || null;
        this.errorData = errorData || null;
    }
}

export interface SendBroadcastSuccess {
    message: string;
    broadcastId: number;
    data: BroadCastConfirmation;
}

/********************/ /********************/
/** GET Broadcast confirmation **/
/********************/ /********************/

export interface BroadCastConfirmation {
    id: number;
    timeStamp: string;
    audience: string;
    message: string;
    languages: string;
    memberCount: string;
    twilioPhone: string;
    sendGridEmail: string;
    channels: string;
}

export interface BroadcastWarnData {
    message: string;
    type: 'language' | 'discardChanges' | '';
}

export interface GetBroadcastHistory {
    message: string;
    data: {
        count: number;
        rows: BroadcastHistory[];
    };
    page: number;
    limit: number;
}

export interface BroadcastHistory {
    id: string;
    audience: string;
    languages: string;
    message: string;
    memberCount: 0;
    broadcast_type: string;
    created_by: string;
    createdAt: string;
    channels: string;
    status: 'In-Progress' | 'Completed' | 'Error';
    organisation: {
        name: string;
    };
    processed: number;
    schedule_time?: string | null;
    sent_date: string;
    tags?: number[];
    reruns?: BroadcastScheduledRerun[];
    parameters_summary?: {
        interests?: string;
        snap_eligible?: string;
        snap_eligibility?: string;
        snap_enrollment?: string;
        snap_interest?: string;
        self_caregiver?: string;
        snap_value?: string;
    };
    attachmentUrl?: string;
    attachment_file_name?: string;
    org_labels?: {
        active: boolean,
        name: string,
    }
}

export interface BroadcastFiltersData {
    individual: boolean;
    audience: any[];
    centers: string[];
    interest_ids?: string[];
    interestNames?: string[];
    labelNames?: string[];
    ph_interested?: {
        value: boolean | null;
    };
    ph_consented?: {
        value: boolean | null;
    };
    ph_info_provided?: {
        value: boolean | null;
    };
    snap_eligibility?: {
        value: boolean | null;
    };
    snap_enrollment?: {
        value: boolean | null;
    };
    snap_interest?: {
        value: boolean | null;
    };
    self_caregiver?: {
        value: SelfCareGiverData;
    };
    /** BH-10704: only people with at least one linked member. Required by @associated_recipients. */
    caregivers_only?: boolean;
    orgLabels?: any[];
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: {
            min: number;
            max: number;
        };
        activationDate?: {
            from: string;
            to: string;
        };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: (string | number)[];
    };
}

export interface BroadcastParameters {
    individual: boolean;
    self_caregiver?: {
        value: SelfCareGiverData;
    };
    caregivers_only?: boolean;
    translated: Translated[];
    snap_eligibility?: {
        value: boolean | null;
    };
    snap_enrollment?: {
        value: boolean | null;
    };
    snap_interest?: {
        value: boolean | null;
    };
    ph_interested?: {
        value: boolean | null;
    };
    ph_consented?: {
        value: boolean | null;
    };
    ph_info_provided?: {
        value: boolean | null;
    };
}
export interface ScheduledSendBroadcast {
    message: string;
    audience: any[];
    centers: string[];
    channels: string[];
    orgId: number;
    schedule_time?: string;
    parameters: BroadcastParameters;
    interest_ids: number[];
    scheduled_broadcast_id?: number;
    preview_start?: {
        date: { rowNumber: string; commDate: string }[];
        offset: string;
        timezone_identifier?: string;
        timezone_name: string;
        is_parent?: boolean;
    };
    tags?: number[];
    reruns?: string[];
    deleted_reruns?: number[];
    updated_reruns?: { id: number; schedule_time: string }[];
    attachment?: any;
    /** Set to true when user removes existing attachment on scheduled broadcast update */
    removeAttachment?: boolean;
    broadcast_type?: string;
    attachmentUrl?: string;
    orgLabels?: any[];
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: {
            min: number;
            max: number;
        };
        activationDate?: {
            from: string;
            to: string;
        };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: (string | number)[];
    };
    outreach_type?: string;
    exclude_previously_contacted?: boolean;
}

/** BH-10704: who a "Linked caregivers" broadcast reaches, from /broadcast/linkedCaregiversSummary. */
export interface LinkedCaregiversSummary {
    sending: number;
    left_out: number;
    not_linked: number;
    no_active_members: number;
    left_out_members: { member_ref: string; reason: 'not_linked' | 'no_active_members' }[];
}

export interface AudienceDedupCheckRequest {
    org_id: string | number;
    outreach_type: string;
    // BH-7556: the raw composed broadcast text (with the @firstname placeholder intact,
    // pre per-recipient substitution). The BE matches it against org message templates so
    // the preview dedup banner only fires for a genuine same-template resend.
    message: string;
    audience: any[];
    centers: string[];
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: { min: number; max: number };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: (string | number)[];
    };
    interest_ids?: number[];
    parameters?: { individual?: boolean };
    orgLabels?: any[];
}

export interface AudienceDedupCheckResponse {
    total_in_audience: number;
    already_sent_count: number;
    already_sent_profile_ids?: number[];
}

export interface ScheduledEditBroadcast {
    id: number;
    audience: string;
    languages: string;
    message: string;
    memberCount: string;
    broadcast_type: string;
    created_by: string;
    createdAt: string;
    channels: string;
    status: string;
    schedule_time: string | null;
    interest_ids: number[];
    org_id: number;
    parameters: BroadcastParameters;
    senior_centers: number[];
    member_details?: {
        member_ref: string;
        fname: string;
        lname: string;
        org_id: number;
        ClientCenters: {
            senior_center_id: number;
            SeniorCenter: { name: string };
        }[];
    }[];
    eligible_count?: number;
    tags?: number[];
}

export interface BroadcastFormData {
    id: number;
    audience: string[];
    languages: string;
    message: string;
    memberCount: string;
    broadcast_type: string;
    created_by: string;
    createdAt: string;
    channels: string[];
    status: string;
    schedule_time: string | null;
    interest_ids?: number[];
    org_id: number;
    parameters: BroadcastParameters;
    senior_centers: number[];
    member_details: SelectorUserData[];
    eligible_count?: number;
    tags?: number[];
    reruns?: BroadcastScheduledRerun[];
    file_name?: string;
    attachmentUrl?: string;
    orgLabels?: any[];
    org_label_ids?: any[]
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: {
            min: number;
            max: number;
        };
        activationDate?: {
            from: string;
            to: string;
        };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: number[];
    };
}

/* SURVEY Scheduled RERUN */
export interface BroadcastScheduledRerun {
    id: string;
    schedule_time: string;
    createdAt: string;
    status: string;
    run_by?: string;
    created_by?: string;
}

export class BroadcastFeatureResolverData {
    formResolverData: BroadcastFormResolverData | null;
    featureLimitData: AccountFeatureLimitsData | null;

    constructor(formResolverData: BroadcastFormResolverData | null = null, featureLimitData: AccountFeatureLimitsData | null = null) {
        this.formResolverData = formResolverData;
        this.featureLimitData = featureLimitData;
    }
}

export class BroadcastFormResolverData {
    broadcastId: number;
    broadcastFormData: BroadcastFormData | null;
    broadcastFilterData: BroadcastFiltersData | null;
    hasNoEligible?: boolean;

    constructor(
        broadcastId: number,
        broadcastFormData: BroadcastFormData | null = null,
        broadcastFilterData: BroadcastFiltersData | null = null
    ) {
        this.broadcastId = broadcastId;
        this.broadcastFormData = broadcastFormData;
        this.broadcastFilterData = broadcastFilterData;
    }
}

export interface SendBroadcastPreviewData {
    message: string;
    data: {
        audience: string;
        languages: string;
        channels: string[];
        interests: string;
        snap_summary: string;
        caregiver_summary: string;
        schedule_time?: string;
        eligiblecount: number;
        longest_message?: { characters: number; segments: number; warn: boolean } | null;
        duplicate_comm?: {
            broadcast_id?: DuplicateCommRecord[];
            survey_ids?: DuplicateCommRecord[];
        } | null;
        orgLabels?: string;

    };
    error?: boolean;
    limit_results?: {
        limit: number;
        is_on_limit: boolean;
        count: number;
        date: string;
    }[];
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: number[];
    };
}

export interface BroadcastPreviewTableDataModel {
    name: string;
    value: string;
    url?: SafeUrl;
    fileType?: string;
    fileName?: string;
    fileSize?: number;
    attachmentUrl?: string;
    isHighlighted?: boolean;
}

export interface BroadcastSentUser {
    member_ref: string;
    fname: string;
    lname: string;
    phone_no: string;
    email: string;
    lang: string;
    broadcast_id: number;
    channel: string;
    delivery_status: string | { class: string; value: string };
    final_status: string | { class: string; value: string };
    timestamp: string;
}
